import { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import InputAdornment from '@material-ui/core/InputAdornment';
import Image from '@base/Image';
import H5 from '@base/H5/H5';
import P2 from '@base/P2/P2';
import TextField from '@base/TextField/TextField';
import Button from '@base/Button/Button';
import Close from '@icon/Close';

const Wrapper = styled.div`
  max-width: 500px;
  margin: auto;
  background-color: var(--color-shadow);
  padding: ${Spacings.SPACING_4B};
  position: relative;
  min-height: calc(100vh - 56px);
  max-height: 600px;

  ${mediaQuery.aboveMobile} {
    background: none;
    min-height: 390px;
    margin: ${Spacings.SPACING_16B} auto;
    padding: 0;
  }
`;

const CardWrapper = styled.div`
  border-radius: ${Spacings.SPACING_2B};
  background-color: var(--color-i-white);
  margin: ${Spacings.SPACING_0B};
  padding: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_4B};
  ${mediaQuery.aboveMobile} {
    box-shadow: 0px 16px 32px var(--color-i-gray-v2);
    padding: ${Spacings.SPACING_6B};
  }
`;

const Description = styled(P2)`
  margin-top: ${Spacings.SPACING_1B};
  margin-bottom: ${Spacings.SPACING_4B};
  color: var(--color-i-text-black);
  ${mediaQuery.aboveMobile} {
    font-size: ${Fonts.P1};
    margin-top: ${Spacings.SPACING_2B};
  }
`;

const StyledTextField = styled(TextField)`
  &.MuiOutlinedInput-root {
    .MuiOutlinedInput-input {
      padding: ${Spacings.SPACING_3B};
      font-size: ${Spacings.SPACING_3B};
      ${mediaQuery.aboveMobile} {
        font-size: ${Fonts.P1};
      }
    }
  }
  &.MuiInputBase-adornedEnd {
    padding-right: ${Spacings.SPACING_4B};
  }

  cursor: pointer;
  ${mediaQuery.aboveMobile} {
    height: 48px;
  }
`;

const StyButton = styled(Button)`
  position: absolute;
  bottom: ${Spacings.SPACING_4B};
  left: 0;
  right: 0;
  width: calc(100% - ${Spacings.SPACING_8B});
  margin: 0 auto;

  ${mediaQuery.aboveMobile} {
    position: static;
    width: 100%;
    margin-top: ${Spacings.SPACING_6B};
  }
`;

const StyH5 = styled(H5)`
  ${mediaQuery.aboveMobile} {
    font-size: ${Fonts.H4};
    font-weight: ${Fonts.HEAVY};
  }
`;

const ClearButton = styled(Close)`
  cursor: pointer;
`;

const GoalCard = ({ goal, goToGoalScreen }) => {
  const endAdornment = () => (
    <InputAdornment position="end">
      <Image src="arrow-down-outline.svg" alt="-" height={16} width={16} />
    </InputAdornment>
  );

  return (
    <CardWrapper>
      <StyH5>Select Goal</StyH5>
      <Description>
        Select the goal for which you want to activate trial
      </Description>
      <StyledTextField
        width="100%"
        helperText={!goal ? 'Please select a goal to continue' : ''}
        placeholder="Select goal"
        error={!goal}
        value={goal?.name}
        onClick={goToGoalScreen}
        endAdornment={endAdornment()}
      />
    </CardWrapper>
  );
};

const TrialReferralInput = ({
  goal,
  goToGoalScreen,
  referralCode,
  isReferralValid,
  checkReferralCodeValidity,
  activateFreeTrial,
  isInProgress
}) => {
  const [code, setCode] = useState(referralCode);
  const [isReferralError, setIsreferralError] = useState(!isReferralValid);

  const handleOnchange = (e) => {
    if (isReferralError && e.target.value !== '') {
      setIsreferralError(false);
    }
    if (!isReferralError && e.target.value === '') {
      setIsreferralError(true);
    }
    setCode(e.target.value);
  };

  const checkReferralCode = useCallback(() => {
    checkReferralCodeValidity(code);
  }, [code, checkReferralCodeValidity]);

  const activateTrial = useCallback(() => {
    if (!code) {
      setIsreferralError(true);
    } else {
      activateFreeTrial(code);
    }
  }, [code, activateFreeTrial]);

  const handleClearInput = () => {
    setCode('');
    setIsreferralError(true);
  };

  const endAdornment = () => {
    if (code)
      return (
        <InputAdornment position="end">
          <ClearButton
            data-testid="clearReferral"
            size="18px"
            onClick={handleClearInput}
          />
        </InputAdornment>
      );
  };

  return (
    <>
      <Wrapper>
        <GoalCard goal={goal} goToGoalScreen={goToGoalScreen} />
        <CardWrapper>
          <StyH5>Activation code</StyH5>
          <Description>
            Enter the activation code to activate your trial
          </Description>
          <StyledTextField
            enterCallback={checkReferralCode}
            error={isReferralError}
            value={code}
            onChange={handleOnchange}
            width="100%"
            placeholder="Enter activation code"
            helperText={
              isReferralError ? ' Please enter a valid activation code' : ''
            }
            endAdornment={endAdornment()}
          />
        </CardWrapper>
        <StyButton
          label={isInProgress ? 'Submitting...' : 'Activate free trial'}
          width="100%"
          variant={isInProgress ? 'disabled' : 'filled'}
          onClick={activateTrial}
        />
      </Wrapper>
    </>
  );
};

export default TrialReferralInput;
